import React from 'react';
import { clsx } from 'clsx';
import Link from 'next/link.js';
import type { CSS } from '@components-shared/src/stitches';
import { styled, theme } from '@components-shared/src/stitches';

const BaseButtonStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none',
  borderRadius: '6px',
  lineHeight: '20px',
  paddingX: '20px',
  paddingY: '12px',
  cursor: 'pointer',
  maxWidth: 'fit-content',
  border: '1.5px solid transparent',
  fontFamily: '$title',
  fontWeight: 500,
  whiteSpace: 'nowrap',

  '&:hover': {
    boxShadow: '0px 2px 4px 0px #0000002E',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  svg: {
    zoom: '0.9',
  },
  compoundVariants: [
    {
      loading: true,
      variant: 'primary',
      css: {
        cursor: 'not-allowed',
        border: 'none !important',
        background: '$neutral400',
        '&:hover': {
          background: '$neutral400',
          boxShadow: 'none',
        },
        '&:focus': {
          '&:hover': {
            background: '$neutral400',
            boxShadow: 'none',
          },
        },
      },
    },
    {
      loading: true,
      variant: 'secondary',
      css: {
        cursor: 'not-allowed',
        border: '1.5px solid $neutral400 !important',
        color: '$neutral400',
        outlineColor: '$neutral400',
        '&:hover, &:focus': {
          background: '$neutral400',
          boxShadow: 'none',
        },
        '&:focus': {
          '&:hover': {
            background: '$neutral400',
            boxShadow: 'none',
          },
        },
      },
    },
    {
      loading: true,
      variant: 'tertiary',
      css: {
        cursor: 'not-allowed',
        border: '1.5px solid $neutral200 !important',
        color: '$neutral300',
        '&:hover, &:focus': {
          boxShadow: 'none',
          background: 'transparent',
        },
      },
    },
    {
      loading: true,
      variant: 'brand',
      css: {
        cursor: 'not-allowed',
        border: 'none !important',
        background: '$brand100',
        '&:hover': {
          background: '$brand100',
          boxShadow: 'none',
        },
        '&:focus': {
          '&:hover': {
            background: '$brand100',
            boxShadow: 'none',
          },
        },
      },
    },
    {
      loading: true,
      variant: 'critical',
      css: {
        cursor: 'not-allowed',
        border: 'none !important',
        background: '$destructive200',
        '&:hover': {
          background: '$destructive200',
          boxShadow: 'none',
        },
        '&:focus': {
          '&:hover': {
            background: '$destructive200',
            boxShadow: 'none',
          },
        },
      },
    },
    {
      loading: true,
      variant: 'text',
      css: {
        cursor: 'not-allowed',
        border: 'none !important',
        color: '$neutral300',
        '&:hover': {
          background: '$neutral300',
          boxShadow: 'none',
        },
        '&:focus': {
          '&:hover': {
            background: '$neutral300',
            boxShadow: 'none',
          },
        },
      },
    },
    {
      loading: true,
      variant: 'fuchsia',
      css: {
        opacity: 0.5,
      },
    },
    {
      darkMode: true,
      variant: 'tertiary',
      css: {
        backgroundColor: 'transparent',
        color: '$white',
        border: '1.5px solid $white',
        '&:hover': {
          backgroundColor: 'transparent',
          border: '1.5px solid $white',
          color: '$white',
        },
        '&:focus': {
          '&:hover': {
            backgroundColor: 'transparent',
            border: '1.5px solid $white',
            color: '$white',
          },
        },
        '&:active, &:focus': {
          border: '2px solid $base800',
        },
      },
    },
  ],
  variants: {
    darkMode: {
      true: {},
    },
    loading: {
      true: {
        cursor: 'not-allowed',
        outline: 'none !important',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    fullWidth: {
      true: {
        width: '100%',
        maxWidth: 'unset',
      },
    },
    size: {
      XS: {
        height: '26px',
        minWidth: '62px',
        paddingX: 0,
        fontSize: 12,
      },
      S: {
        height: '36px',
        fontSize: '14px',
      },
      M: {
        minWidth: '103px',
        height: '40px',
        fontSize: '14px',
      },
      L: {
        minWidth: '109px',
        height: '44px',
        fontSize: '16px',
      },
      XL: {
        minWidth: '112px',
        fontSize: '16px',
        height: '52px',
      },

      full: {
        maxWidth: '100%',
      },

      squared: {
        width: '40px',
        height: '40px',
        fontSize: '14px',
      },
    },

    hasIcon: {
      true: {
        display: 'flex',
        alignItems: 'center',
      },
    },
    variant: {
      primary: {
        background: '$neutral800',
        color: 'white',
        '&:hover': {
          background: 'black',
          boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.18)',
        },
        '&:focus': {
          outline: '2px solid $neutral800',
          border: '1.5px solid white',
          '&:hover': {
            background: 'black',
          },
        },
        '&:disabled': {
          background: '$neutral400',
        },
      },
      secondary: {
        background: 'white',
        color: '$neutral800',
        border: '1px solid $neutral800',
        '&:focus': {
          outline: '2px solid $neutral800',
          backgroundColor: 'white',
        },
        '&:disabled': {
          color: '$neutral400',
          borderColor: '$neutral400',
          outlineColor: '$neutral400',
        },
        '&:hover': {
          backgroundColor: '$base100',
        },
      },
      tertiary: {
        backgroundColor: 'white',
        color: '$neutral800',
        textTransform: 'none',
        border: '1px solid $neutral200',
        '&:focus': {
          borderColor: '$neutral400',
          border: '1.5px solid $base800',
        },
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: '$base100',
          border: '1px solid transparent',
        },
        '&:disabled': {
          color: '$neutral300',
          '&:hover': {
            backgroundColor: 'white',
            textTransform: 'none',
            border: '1px solid $neutral200',
          },
        },
      },
      brand: {
        background: '$brand300',
        color: 'white',
        outline: 'none',
        '&:hover': {
          background: '$brand500',
        },
        '&:focus': {
          outline: '2px solid $brand300',
          border: '1.5px solid white',
          '&:hover': {
            outlineColor: '$brand500',
          },
        },
        '&:disabled': {
          background: '$brand100',
        },
      },
      critical: {
        background: '$destructive300',
        color: 'white',
        outline: 'none',
        '&:hover': {
          background: '$destructive700',
          boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.18)',
        },
        '&:focus': {
          background: '$destructive300',
          outline: '2px solid $destructive300',
          border: '2px solid white',
          '&:hover': {
            outlineColor: '$destructive300',
          },
        },
        '&:disabled': {
          background: '$destructive200',
        },
      },
      text: {
        background: 'transparent',
        color: '$neutral800',
        '&:hover': {
          background: '$neutral100',
          boxShadow: 'none',
        },
        '&:disabled': {
          color: '$neutral300',
          '&:hover': {
            background: 'transparent',
          },
        },
      },
      link: {
        background: 'transparent',
        display: 'inline',
        padding: 0,
        height: 'auto',
        minWidth: 'unset',
        color: '$neutral800',
        textDecoration: 'underline',
        textUnderlineOffset: '4px',
        '&:hover': {
          color: '$neutral900',
          boxShadow: 'none',
        },
        '&:active': {
          color: '$neutral900',
          boxShadow: 'none',
        },
        '&:focused': {
          color: '$neutral900',
          boxShadow: 'none',
        },
        '&:disabled': {
          color: '$neutral300',
          pointerEvents: 'none',
          cursor: 'not-allowed',
          '&:hover': {
            background: 'transparent',
          },
        },
      },
      fuchsia: {
        background: theme.gradients.fuchsia,
        backgroundPosition: 'center',
        backgroundSize: '200%',
        color: '#fff',
        '&:focus': {
          outline: '2px solid #EB5CBB',
          border: '1.5px solid white',
        },

        '&:disabled': {
          opacity: 0.5,
        },
      },
      green: {
        background: '$brand500',
        borderRadius: '$extraLarge',
        color: '$white',

        '&:hover, &:focus': {
          background: '$brand600',
        },
      },
    },
  },
  defaultVariants: {
    size: 'L',
  },
};

const BaseButton = styled('button', BaseButtonStyles);

type IconProps = { children: React.ReactNode; iconOnly?: boolean };

const LeadingIcon = ({ children, iconOnly }: IconProps) => {
  return (
    <div
      data-testid="button-leading-icon"
      className={clsx('flex items-center', !iconOnly && 'mr-2')}
    >
      {children}
    </div>
  );
};

const TrailingIcon = ({ children, iconOnly }: IconProps) => {
  return (
    <div data-testid="button-trailing-icon" className={iconOnly ? '' : 'ml-2'}>
      {children}
    </div>
  );
};

export type ButtonProps = {
  variant:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'brand'
    | 'critical'
    | 'text'
    | 'fuchsia'
    | 'green'
    | 'link';
  darkMode?: boolean;
  label: string;
  disabled?: boolean;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  size?: 'XS' | 'S' | 'M' | 'L' | 'XL' | 'full' | 'squared';
  fullWidth?: boolean;
  href?: string;
  /**
   * @deprecated Extend Button component instead with styled(Button) syntax
   */
  deprecatedCSS?: CSS;
  testId?: string;
  fullWidthAnchor?: boolean;
  iconOnly?: boolean;
  className?: string;
} & React.ComponentProps<typeof BaseButton>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    leadingIcon,
    trailingIcon,
    testId,
    fullWidthAnchor = false,
    iconOnly = false,
    deprecatedCSS,
    className,
    ...props
  },
  forwardedRef,
) {
  const loadingAnimation = (
    <div
      className={clsx(
        'loading-circle inline-block aspect-square flex-grow-0 animate-spin rounded-full border-2 border-solid',
        props.variant === 'primary' && 'border-base100 border-l-neutral400  border-t-neutral400',
        (props.variant === 'secondary' ||
          props.variant === 'tertiary' ||
          props.variant === 'text') &&
          'border-white  border-l-neutral400 border-t-neutral400 ',
        (props.variant === 'brand' || props.variant === 'green') &&
          'border-base50 border-l-brand100 border-t-brand100',
        props.variant === 'critical' &&
          'border-base50 border-l-destructive200 border-t-destructive200',
        props.variant === 'fuchsia' && 'border-base50 border-l-fuchsia500 border-t-fuchsia500',
        (iconOnly || props.size === 'squared') && 'ml-0 aspect-square h-4/5',
        props.size === 'XS' && 'ml-2 h-2.5 w-2.5 !border-1.5',
        (props.size === 'S' || props.size === 'M' || props.size === 'full') && 'ml-1 h-3.5 w-3.5',
        (props.size === 'L' || props.size === 'XL' || !props.size) && 'ml-1 h-4 w-4',
      )}
    />
  );

  const href = props?.href;
  const isExternal = href?.startsWith('http') || href?.startsWith('mailto');
  const rel = isExternal ? 'noopener noreferrer' : undefined;
  const target = isExternal ? '_blank' : undefined;

  const Component = (
    <BaseButton
      aria-label={props.label}
      aria-disabled={props.disabled}
      hasIcon={!!leadingIcon || !!trailingIcon}
      disabled={props.disabled}
      loading={props.loading}
      darkMode={props.darkMode}
      data-testid={testId}
      css={deprecatedCSS}
      {...props}
      ref={forwardedRef}
      onClick={
        props.loading
          ? undefined
          : (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.currentTarget.blur();

              if (props.onClick) {
                props.onClick(e);
              }
            }
      }
      className={className}
    >
      {leadingIcon && !props.loading && (
        <LeadingIcon iconOnly={iconOnly}>{leadingIcon}</LeadingIcon>
      )}
      {iconOnly ? null : props.label}
      {!!trailingIcon && !props.loading && (
        <TrailingIcon iconOnly={iconOnly}>{trailingIcon}</TrailingIcon>
      )}
      {props.loading ? loadingAnimation : null}
    </BaseButton>
  );

  return (
    <>
      {props?.href ? (
        <StyledLink
          href={props.href}
          data-testid={testId}
          data-href={props.href}
          rel={rel}
          role="link"
          target={target}
          fullWidthAnchor={fullWidthAnchor}
        >
          {Component}
        </StyledLink>
      ) : (
        Component
      )}
    </>
  );
});

const StyledLink = styled(Link, {
  color: 'unset',
  textDecoration: 'unset',

  variants: {
    fullWidthAnchor: {
      true: {
        width: '100%',
      },
    },
  },
});
